import { KeyRound, ShieldCheck } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/auth/useAuth";
import { Field } from "@/components/Field";
import { CHANGE_PASSWORD_SCOPES } from "@/config/msalConfig";
import { AppShell } from "@/layout/AppShell";
import { changeMyPassword, GraphError } from "@/services/graphService";
import { isCiamTenant } from "@/utils/tenant";

type Status =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "error"; message: string; code?: string }
  | { kind: "success" };

/**
 * Password-policy evaluation done entirely client-side as a UX hint.
 * The ground truth is the tenant password policy enforced by Entra
 * on the POST /me/changePassword call.
 */
type PolicyCheck = { label: string; ok: boolean };

function evaluatePolicy(pw: string, current: string): PolicyCheck[] {
  return [
    { label: "At least 8 characters", ok: pw.length >= 8 },
    { label: "Contains a lowercase letter", ok: /[a-z]/.test(pw) },
    { label: "Contains an uppercase letter", ok: /[A-Z]/.test(pw) },
    { label: "Contains a digit", ok: /\d/.test(pw) },
    {
      label: "Contains a symbol",
      ok: /[^A-Za-z0-9]/.test(pw),
    },
    {
      label: "Is not the same as your current password",
      ok: pw.length === 0 || pw !== current,
    },
  ];
}

export function ChangePasswordPage() {
  return (
    <AppShell>
      <ChangePasswordContent />
    </AppShell>
  );
}

function ChangePasswordContent() {
  const { account, acquireToken } = useAuth();
  const ciam = isCiamTenant();

  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [status, setStatus] = useState<Status>({ kind: "idle" });

  const policy = useMemo(() => evaluatePolicy(next, current), [next, current]);
  const allPolicyOk = policy.every((p) => p.ok);
  const matches = next.length > 0 && next === confirm;
  const canSubmit =
    status.kind !== "submitting" &&
    current.length > 0 &&
    next.length > 0 &&
    confirm.length > 0 &&
    matches &&
    allPolicyOk;

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus({ kind: "submitting" });
    try {
      const token = await acquireToken(CHANGE_PASSWORD_SCOPES);
      if (!token?.accessToken) {
        setStatus({
          kind: "error",
          message:
            "Could not acquire an access token with Directory.AccessAsUser.All. Your administrator must consent to this permission on the SPA app registration before passwords can be changed from the portal.",
        });
        return;
      }
      await changeMyPassword(token.accessToken, current, next);
      setCurrent("");
      setNext("");
      setConfirm("");
      setStatus({ kind: "success" });
    } catch (err) {
      if (err instanceof GraphError) {
        setStatus({
          kind: "error",
          code: err.code,
          message: humanizeGraphError(err),
        });
      } else {
        setStatus({
          kind: "error",
          message: err instanceof Error ? err.message : "Password change failed.",
        });
      }
    }
  };

  return (
    <div className="mx-auto flex max-w-xl flex-col gap-6">
      <header>
        <Link
          to="/myapps"
          className="text-xs font-medium text-brand-700 hover:underline"
        >
          ← Back to My Apps
        </Link>
        <h1 className="mt-2 flex items-center gap-2 text-2xl font-semibold text-slate-900">
          <KeyRound className="h-6 w-6 text-brand-600" />
          Change password
        </h1>
        <p className="mt-1 text-sm text-slate-600">
          Signed in as{" "}
          <span className="font-medium text-slate-900">
            {account?.name || account?.username}
          </span>
          . You'll stay signed in after changing your password.
        </p>
      </header>

      <form onSubmit={onSubmit} className="card space-y-4 p-6">
        <Field
          label="Current password"
          type="password"
          value={current}
          onChange={setCurrent}
          autoComplete="current-password"
        />
        <Field
          label="New password"
          type="password"
          value={next}
          onChange={setNext}
          autoComplete="new-password"
        />
        <Field
          label="Confirm new password"
          type="password"
          value={confirm}
          onChange={setConfirm}
          autoComplete="new-password"
          error={
            confirm.length > 0 && !matches
              ? "Passwords do not match."
              : null
          }
        />

        <PolicyList checks={policy} />

        {status.kind === "error" ? (
          <ErrorBox message={status.message} code={status.code} />
        ) : null}
        {status.kind === "success" ? <SuccessBox /> : null}

        <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-4">
          <button
            type="submit"
            disabled={!canSubmit}
            className="btn-primary"
          >
            {status.kind === "submitting" ? "Changing…" : "Change password"}
          </button>
        </div>
      </form>

      <AdminGuidance ciam={ciam} />
    </div>
  );
}

function PolicyList({ checks }: { checks: PolicyCheck[] }) {
  return (
    <ul className="rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700">
      <div className="mb-1 font-semibold text-slate-900">
        Password requirements
      </div>
      {checks.map((c) => (
        <li key={c.label} className="flex items-center gap-2">
          <span
            className={c.ok ? "text-emerald-600" : "text-slate-400"}
            aria-hidden="true"
          >
            {c.ok ? "✓" : "○"}
          </span>
          <span className={c.ok ? "text-slate-700" : "text-slate-500"}>
            {c.label}
          </span>
        </li>
      ))}
      <li className="mt-2 text-[11px] italic text-slate-500">
        Your tenant may enforce additional rules — the final check runs
        server-side on save.
      </li>
    </ul>
  );
}

function ErrorBox({ message, code }: { message: string; code?: string }) {
  return (
    <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
      <div className="font-semibold">Password change failed</div>
      {code ? (
        <div className="mt-0.5 text-xs font-mono text-red-700">{code}</div>
      ) : null}
      <div className="mt-1 text-xs">{message}</div>
    </div>
  );
}

function SuccessBox() {
  return (
    <div className="flex items-start gap-2 rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
      <ShieldCheck className="mt-0.5 h-4 w-4" />
      <div>
        <div className="font-semibold">Password changed</div>
        <div className="text-xs">
          Your new password is active on all devices. Existing sessions
          remain signed in.
        </div>
      </div>
    </div>
  );
}

function AdminGuidance({ ciam }: { ciam: boolean }) {
  return (
    <details className="rounded border border-slate-200 bg-white p-3 text-xs text-slate-600">
      <summary className="cursor-pointer font-semibold text-slate-800">
        Administrator setup
      </summary>
      <div className="mt-2 space-y-2">
        <p>
          The portal calls Microsoft Graph{" "}
          <code>POST /me/changePassword</code>. The SPA app registration
          needs the delegated permission{" "}
          <code>Directory.AccessAsUser.All</code> with tenant admin
          consent.
        </p>
        {ciam ? (
          <p>
            This is a CIAM / Entra External ID tenant. The
            changePassword endpoint is supported for customer identities
            once admin consent is in place. If users receive
            <code>Authorization_RequestDenied</code>, consent has not
            been granted yet.
          </p>
        ) : (
          <p>
            This is a workforce tenant. Users with MFA requirements may
            be challenged interactively before the password change
            completes.
          </p>
        )}
        <p>
          If your tenant uses "Forgot my password" self-service, you can
          also set <code>VITE_PASSWORD_RESET_URL</code> in{" "}
          <code>wrangler.jsonc</code> to link users to Entra's reset
          flow from the profile page.
        </p>
      </div>
    </details>
  );
}

function humanizeGraphError(err: GraphError): string {
  const msg = err.message || "Graph refused the password change.";
  switch (err.code) {
    case "Authorization_RequestDenied":
      return (
        "Your administrator has not consented to the permission needed to change your password from the portal (Directory.AccessAsUser.All). " +
        "Please contact your administrator. Original message: " +
        msg
      );
    case "Request_BadRequest":
      if (/current password/i.test(msg) || err.status === 401) {
        return "Your current password is incorrect.";
      }
      if (/complexity|history|password/i.test(msg)) {
        return `The new password does not satisfy the tenant password policy. ${msg}`;
      }
      return msg;
    case "InvalidAuthenticationToken":
      return "Your sign-in session expired. Please sign in again and retry.";
    default:
      return msg;
  }
}
